import { createSelectorCreator, defaultMemoize } from 'reselect'
import isEqual from 'lodash/isEqual'
import _isNil from 'lodash/isNil'
import _join from 'lodash/join'
import { IntlShape } from 'react-intl'

import { Home as SwcHome } from '@racepointenergy/swc/scs/types/home'

import type {
  SubscriptionPlan,
  StripeSubscriptionItem,
  LicenseItem,
} from './components/HomeSubscription/types'
import { subscriptionInfoMessages } from './components/HomeSubscription/messages'
import { Home } from './types'
import { ESSENTIAL_PLAN, ESSENTIAL_PLAN_TO_MIGRATE, NO_PLAN } from './consts'

export const prettyVersionNumber = (versionNumber: number) => {
  const numberLength = `${versionNumber}`.length

  return _join(
    [
      `${parseInt(`${versionNumber}`.substring(0, numberLength - 11), 10)}`,

      `${parseInt(
        `${versionNumber}`.substring(numberLength - 11, numberLength - 11 + 3),
        10
      )}`,
      `${parseInt(
        `${versionNumber}`.substring(numberLength - 8, numberLength - 8 + 3),
        10
      )}`,
      `${parseInt(`${versionNumber}`.substring(numberLength - 5))}`,
    ],
    '.'
  )
}

export const createDeepEqualSelector = createSelectorCreator(
  defaultMemoize,
  isEqual
)

const RELEASE_SET_EXTENSIONS =
  /\.mpkg|smhost2|smhost3|2champhost|2chhost|3chhost|smhost|simhost|zip|amphost|keyhost|dirhost[0-9]\\?/i

const substringEndsWithValidExtension = (substr: string) =>
  substr.match(RELEASE_SET_EXTENSIONS)

export const releaseSetFilename = (downloadUrl: string) =>
  downloadUrl.split(/[?/]/).find(substringEndsWithValidExtension)

export const mapStripeSubscriptionToPlan = (stripeRawSubscription: {
  items: { data: StripeSubscriptionItem[] }
  nonMigratedLicenses: LicenseItem[]
  id: string
}): SubscriptionPlan => {
  const stripePlan = stripeRawSubscription?.items?.data[0]?.plan
  if (stripePlan) {
    return {
      ...ESSENTIAL_PLAN,
      id: stripePlan.id,
      nickname: stripePlan.nickname,
      price: stripePlan.amount,
      interval: stripePlan.interval,
    }
  }

  if (
    stripeRawSubscription?.nonMigratedLicenses?.length > 0 &&
    _isNil(stripeRawSubscription.id)
  ) {
    return ESSENTIAL_PLAN_TO_MIGRATE
  }

  return NO_PLAN
}

export const getSubscriptionPlanName = (
  plan: SubscriptionPlan,
  formatMessage: IntlShape['formatMessage']
): string | null => {
  return [ESSENTIAL_PLAN.type, ESSENTIAL_PLAN_TO_MIGRATE.type].includes(
    plan?.type
  )
    ? formatMessage(subscriptionInfoMessages.essentialPlanName)
    : null
}

export const getHomeSubscriptonInfo = (home: Home | null) => {
  if (home?.subscriptionInfo) {
    return (
      home.subscriptionInfo.find((option) => option.category === 'ESSENTIALS') ??
      null
    )
  }
  return null
}

export const getHomeFullSubscriptonInfo = (home: Home | null) => {
  if (home?.subscriptionInfo) {
    return home?.subscriptionInfo ? home.subscriptionInfo : null
  } else {
    return null
  }
}

export const getHomeNetworkSubscriptonInfo = (home: Home | null) => {
  if (home?.subscriptionInfo) {
    return (
      home?.subscriptionInfo.find((option) => option.category === 'NETWORK') ||
      null
    )
  } else {
    return null
  }
}

export const isStripeManaged = (home: Home | null) => {
  const subscriptionInfo = getHomeSubscriptonInfo(home)
  return subscriptionInfo?.authority === 'billing'
}

export const hasSystemAccessEnabled = (
  home: Home | SwcHome | null
): boolean => {
  return !!home && home.configAccessExpires > Date.now()
}

export const createDropdownOption = (
  label: string,
  value: string | boolean | number
) => ({ label, value })

export const generateUiDeviceOptions = (
  list: string[],
  unselectedLabel: string,
  isIndexAsValue = false
) => {
  const roomsOptions = [createDropdownOption(unselectedLabel, '')]
  list.forEach((el, index) => {
    roomsOptions.push(
      createDropdownOption(
        removeUnderlines(el),
        isIndexAsValue ? `${index}` : el
      )
    )
  })
  return roomsOptions
}

export const removeUnderlines = (string: string) => {
  const result = string.split('_').join(' ')
  return result
}

export const checkVersion = (versionString: string) => {
  if (versionString === '') {
    return false
  }
  const requiredVersion = '10.2.3'
  const versionParts = versionString.split('-')[0].split('.')
  const requiredParts = requiredVersion.split('.')

  for (let i = 0; i < versionParts.length; i++) {
    const currentPart = parseInt(versionParts[i], 10) || 0
    const requiredPart = parseInt(requiredParts[i], 10) || 0

    if (currentPart > requiredPart) {
      return true
    } else if (currentPart < requiredPart) {
      return false
    }
  }

  return true
}
